import type { UIMessage } from 'ai';
import { Brain, X } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';

import {
  useAnswerMemoryProposal,
  useOpenMemoryProposals,
} from '../../api/use-memory-proposals';
import {
  type MemoryProposalCard,
  asToolPart,
  readMemoryProposal,
} from '../../lib/tool-parts';

/**
 * Memories Claire parked BESIDE the chat, kept out of the conversation.
 *
 * `meta_remember` and `meta_suggestMemory` (web only)
 * no longer ask in the chat: they return a `memory_suggestion` with a
 * `proposal` token, the transcript and trace skip that tool part
 * (`isSideOfChatToolPart`), and Claire is told to carry on as if nothing was
 * suggested. This tray is the only place it shows: a small card in the
 * top-right corner, answered with a click straight against the API. No chat
 * message is sent, so answering it never interrupts or steers the chat.
 *
 * Deliberately small: a "Save to Claire's Memory?" heading, the memory, and Save /
 * Reject (the X rejects too). Who it applies to and where live on the
 * memories settings page, where they can be changed.
 */

const STACK_ID = 'claire-memory-proposals';
/** More than this at once is noise; the rest wait until these are answered. */
const MAX_SHOWN = 3;

/**
 * One fixed container on `body`: the chat is several scroll containers deep,
 * and a `fixed` child of a transformed ancestor is not fixed to the viewport.
 * `top-28` clears the app header and the conversation bar under it.
 */
function stackRoot(): HTMLElement {
  const existing = document.getElementById(STACK_ID);
  if (existing) return existing;
  const root = document.createElement('div');
  root.id = STACK_ID;
  root.setAttribute('aria-live', 'polite');
  root.className =
    'pointer-events-none fixed inset-x-3 top-28 z-40 flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:w-[300px]';
  document.body.appendChild(root);
  return root;
}

/**
 * Whether the tray is showing anything, for the chat layout: on wide screens
 * the chat column makes room so a card never covers the end of Claire's reply.
 */
let trayOpen = false;
const trayListeners = new Set<() => void>();
function setTrayOpen(open: boolean) {
  if (trayOpen === open) return;
  trayOpen = open;
  for (const listener of trayListeners) listener();
}
function subscribeTray(listener: () => void) {
  trayListeners.add(listener);
  return () => {
    trayListeners.delete(listener);
  };
}

/** True while at least one memory proposal is showing beside the chat. */
export function useMemoryProposalTrayOpen(): boolean {
  return useSyncExternalStore(
    subscribeTray,
    () => trayOpen,
    () => false
  );
}

/** Every side-of-chat proposal in the conversation, oldest first. */
export function collectMemoryProposals(
  messages: readonly UIMessage[]
): MemoryProposalCard[] {
  const found: MemoryProposalCard[] = [];
  for (const message of messages) {
    if (message.role !== 'assistant') continue;
    for (const part of message.parts) {
      const tp = asToolPart(part);
      const proposal = tp ? readMemoryProposal(tp) : null;
      if (proposal) found.push(proposal);
    }
  }
  return found;
}

export function MemoryProposalTray({
  messages,
  placement = 'floating',
}: {
  messages: readonly UIMessage[];
  placement?: 'floating' | 'inline';
}) {
  // The AI SDK can mutate a streaming message's parts without replacing the
  // messages array, so scan on every render to pick up completed tool calls.
  const proposals = collectMemoryProposals(messages);
  const tokens = proposals.map((p) => p.token);
  // Answered or expired server-side (another tab, before a reload) stays gone.
  const { open } = useOpenMemoryProposals(tokens);
  const [answered, setAnswered] = useState<ReadonlySet<string>>(new Set());
  const now = useNow();

  const openSet = new Set(open ?? []);
  const seen = new Set<string>();
  const shown = proposals
    .filter((p) => {
      if (seen.has(p.token)) return false;
      seen.add(p.token);
      return (
        openSet.has(p.token) &&
        !answered.has(p.token) &&
        new Date(p.expiresAt).getTime() > now
      );
    })
    .slice(-MAX_SHOWN)
    .reverse();

  const hasCards = shown.length > 0;
  useEffect(() => {
    if (placement === 'floating') setTrayOpen(hasCards);
  }, [hasCards, placement]);
  useEffect(
    () => () => {
      if (placement === 'floating') setTrayOpen(false);
    },
    [placement]
  );

  const markAnswered = (token: string) =>
    setAnswered((prev) => new Set(prev).add(token));
  const restoreUnanswered = (token: string) =>
    setAnswered((prev) => {
      const next = new Set(prev);
      next.delete(token);
      return next;
    });

  if (!hasCards) return null;
  const cards = shown.map((proposal) => (
    <MemoryProposalItem
      key={proposal.token}
      proposal={proposal}
      onAnswered={markAnswered}
      onRestore={restoreUnanswered}
    />
  ));
  if (placement === 'inline') {
    return (
      <div
        aria-live="polite"
        className="mx-4 mt-2 flex max-h-[40vh] shrink-0 flex-col gap-2 overflow-y-auto"
      >
        {cards}
      </div>
    );
  }
  if (typeof document === 'undefined') return null;
  return createPortal(cards, stackRoot());
}

function MemoryProposalItem({
  proposal,
  onAnswered,
  onRestore,
}: {
  proposal: MemoryProposalCard;
  onAnswered: (token: string) => void;
  onRestore: (token: string) => void;
}) {
  const { answerMemoryProposalAsync } = useAnswerMemoryProposal();
  const [saving, setSaving] = useState(false);

  const save = async (replace: boolean) => {
    setSaving(true);
    try {
      await answerMemoryProposalAsync({
        token: proposal.token,
        decision: 'save',
        replace,
      });
      onAnswered(proposal.token);
      toast.success('Saved to memory');
    } catch (error) {
      setSaving(false);
      const status = (error as { status?: number } | null)?.status;
      // Gone (expired, or answered elsewhere): nothing left to save.
      if (status === 404) onAnswered(proposal.token);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Could not save that memory'
      );
    }
  };

  const dismiss = () => {
    // Hide at once, but restore the card if the server never recorded the no.
    onAnswered(proposal.token);
    void answerMemoryProposalAsync({
      token: proposal.token,
      decision: 'dismiss',
    }).catch((error: unknown) => {
      onRestore(proposal.token);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Could not dismiss that memory'
      );
    });
  };

  return (
    <section
      aria-labelledby={`memory-proposal-${proposal.token}`}
      data-testid="claire-memory-proposal"
      className="pointer-events-auto w-full rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg duration-200 animate-in fade-in slide-in-from-right-4"
    >
      <div className="flex items-center gap-2">
        <Brain className="size-4 shrink-0 text-primary" aria-hidden />
        <h2
          id={`memory-proposal-${proposal.token}`}
          className="flex-1 text-sm font-medium"
        >
          Save to Claire's Memory?
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="-mr-1 size-6 shrink-0 text-muted-foreground"
          aria-label="Dismiss"
          disabled={saving}
          onClick={dismiss}
        >
          <X className="size-3.5" />
        </Button>
      </div>
      <MemoryText content={proposal.content} />
      <div className="mt-3 flex justify-end gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2.5 text-xs"
          disabled={saving}
          onClick={dismiss}
        >
          Reject
        </Button>
        {proposal.conflictsWith ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2.5 text-xs"
            disabled={saving}
            onClick={() => void save(true)}
            title={`Replaces "${proposal.conflictsWith.content}"`}
          >
            Replace old
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          className="h-7 px-3 text-xs"
          disabled={saving}
          onClick={() => void save(false)}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </section>
  );
}

/**
 * The memory on its grey box: two lines ending in an ellipsis, with "See more"
 * underneath when it runs longer (like a long WhatsApp message). Memories are
 * capped at 255 characters, so expanded it stays a few lines at most.
 */
function MemoryText({ content }: { content: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Measure while clamped: only a memory that is actually cut off gets the
  // link. A proposal's content never changes, so this runs on mount and after
  // collapsing.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  return (
    <div className="mt-2 rounded-md bg-muted px-3 py-2 text-sm leading-5">
      <p
        ref={ref}
        data-testid="claire-memory-proposal-text"
        className={`whitespace-pre-wrap break-words ${expanded ? '' : 'line-clamp-2'}`}
      >
        {content}
      </p>
      {overflows || expanded ? (
        <button
          type="button"
          className="mt-0.5 text-xs font-medium text-primary hover:underline focus-visible:underline focus-visible:outline-none"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      ) : null}
    </div>
  );
}

/** The current time, ticking each minute so an expired card goes away. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}
